import { useEffect, useRef, useState } from "react";
import { Field } from "react-final-form";
import { List } from "antd";
import { useDebounce } from "usehooks-ts";
import cn from "classnames";
import { disabledValidate } from "~/utils";
import { NamesOfFieldBankInfoEnum, NamesOfFieldEnum, TypeBankInfo } from "~/typings/types";
import { useGetBankInfo } from "../hooks/useGetBankInfo";
import { FloatingLabelInput } from "~/components";
import { ERROR_FIELD_ } from "~/configs/consts";

import styles from "./Autocomplete.module.scss";

export const AutocompleteInput = ({
  name,
  titleField,
  required,
  validate,
  disabled,
  tooltip,
  descriptionField,
  setErrorRequest,
  handleSelectedSuggestions,
  urlGetBankInfo,
  customError,
  setIsTooltipVisible,
  isConfirmed,
  registerReset,
}: any) => {
  const [query, setQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [searchedWithNoResults, setSearchedWithNoResults] = useState(false);
  const justSelectedRef = useRef(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const listItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inputRef = useRef<any>(null);

  const { suggestions, setSuggestions, isPendingResponseSuggestions, fetchSuggestions } =
    useGetBankInfo(setErrorRequest, urlGetBankInfo);

  const debouncedQuery = useDebounce(query, 1000);

  useEffect(() => {
    registerReset?.(() => {
      justSelectedRef.current = false;
      setQuery("");
      setSearchedWithNoResults(false);
      setSuggestions([]);
    });
  }, []);

  useEffect(() => {
    if (debouncedQuery.length > 3) {
      fetchSuggestions(debouncedQuery);
    } else {
      setSuggestions([]);
      setSearchedWithNoResults(false);
    }
  }, [debouncedQuery]);

  useEffect(() => {
    if (suggestions.length > 0) {
      setFocusedIndex(0);
      setSearchedWithNoResults(false);
    } else if (debouncedQuery.length > 3 && !isPendingResponseSuggestions && !justSelectedRef.current) {
      setFocusedIndex(-1);
      setSearchedWithNoResults(true);
    } else {
      setFocusedIndex(-1);
    }

    if (suggestions.length === 1 && /^\d{9}$/.test(query.trim()) && inputRef.current) {
      handleSuggestionClick(suggestions[0], inputRef.current);
    }
  }, [suggestions, isPendingResponseSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (focusedIndex !== -1 && listItemRefs.current[focusedIndex]) {
      listItemRefs.current[focusedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  }, [focusedIndex]);

  const handleInputChange = (inputValue: string) => {
    if (inputValue.trim() === "") {
      return;
    }
    setQuery(inputValue);
    setFocusedIndex(-1);
  };

  const handleSuggestionClick = (suggestion: TypeBankInfo, input: any) => {
    justSelectedRef.current = true;
    input.onChange(suggestion[NamesOfFieldEnum.BankBic]);
    handleSelectedSuggestions(suggestion);
    setSuggestions([]);
    setFocusedIndex(-1);
    setSearchedWithNoResults(false);
  };

  const handleKeyDown = (event: any, input: any) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusedIndex(prevIndex => Math.min(prevIndex + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusedIndex(prevIndex => Math.max(prevIndex - 1, 0));
    } else if (event.key === "Enter" && focusedIndex >= 0 && suggestions.length > 0) {
      event.preventDefault();
      handleSuggestionClick(suggestions[focusedIndex], input);
    }
  };

  return (
    <Field
      name={name}
      validate={disabledValidate(validate)}
      subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
    >
      {({ input, meta: { submitFailed, error, submitError } }) => {
        inputRef.current = input;
        const hasError =
          customError || (error || submitError) && submitFailed;
        const inputId = hasError ? `${ERROR_FIELD_ + input.name}` : undefined;

        return (
          <div style={{ width: "100%", margin: "0 auto", position: "relative" }}>
            <div className={styles.containerInput}>
              <FloatingLabelInput
                disabled={disabled || isConfirmed}
                {...input}
                titleField={titleField}
                required={required}
                tooltip={isConfirmed ? undefined : tooltip}
                id={inputId}
                isLoading={isPendingResponseSuggestions}
                classNameInput={cn(styles.inputCustom, {
                  [styles.inputCustomError]: hasError
                })}
                onChange={event => {
                  input.onChange(event);
                  handleInputChange(event.target.value);
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(event, input)}
                onBlurCallback={() => setIsTooltipVisible(false)}
                onFocusCallback={() => setIsTooltipVisible(true)}
              />
            </div>
            {suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                style={{
                  marginTop: "4px",
                  width: "100%",
                  maxHeight: "150px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  zIndex: 1000
                }}
              >
                <List
                  dataSource={suggestions}
                  renderItem={(suggestion, index) => (
                    <List.Item
                      key={index}
                      ref={el => (listItemRefs.current[index] = el)}
                      onClick={() => handleSuggestionClick(suggestion, input)}
                      className={focusedIndex === index ? styles.focusedListItem : styles.listItem}
                      onMouseEnter={() => setFocusedIndex(index)}
                    >
                      {`${suggestion[NamesOfFieldBankInfoEnum.BankBic]} | ${suggestion[NamesOfFieldBankInfoEnum.BankName]}`}
                    </List.Item>
                  )}
                />
              </div>
            )}
            {!input.value && descriptionField && (
              <p className={styles.description}>{descriptionField}</p>
            )}
            {searchedWithNoResults && !isPendingResponseSuggestions && suggestions.length === 0 && input.value && !isConfirmed && (
              <p className={styles.noResults}>Банк не найден. Проверьте БИК или заполните поля вручную.</p>
            )}
          </div>
        )
      }}
    </Field>
  );
};
